import {
  BillingInfoPlan,
  Org,
  OrgInfoSubscriptionType,
  PaywallPopupReason,
  PopupType,
  User,
} from '@nx-frontend/shared/types';
import { BillingInfo } from '@nx-frontend/shared/types';
import { AiUsage } from '@nx-frontend/shared/types';
import { OpenModalFn } from '@nx-frontend/shared/types';
import { GetOpenPayWallPopupProps } from '@nx-frontend/shared/types';
import { GetMessageTask, b64DecodeUnicode } from '@nx-frontend/shared/utils';

interface ShowAILmitPopupParams {
  openModal: OpenModalFn;
  subscriptionType: OrgInfoSubscriptionType;
  billingInfo: BillingInfo;
  currentOrganization: Org;
  user: User;
  usage?: AiUsage;
  getOpenPaywallPopup: (props: GetOpenPayWallPopupProps) => void;
  openGenAiAddonPopup: () => void;
}

/**
 * Shows AI addon upgrade options for orgs that don't have it.
 * Contains three scenarios:
 * 1. Current user is current org owner and has no subscriptions, we show the common upgrade popup
 * 2. Current user is current org owner and has a subscription, we show message with AI addon price for the org and a button to upgrade
 * 3. Current user is not an owner of the current org, we show message that they need to contact the owner to upgrade
 * @param params
 * @deprecated use `useShowAiLimitPopup` instead
 */
export function showAiLimitPopup(params: ShowAILmitPopupParams) {
  const { openModal, currentOrganization, user, billingInfo, getOpenPaywallPopup, openGenAiAddonPopup } = params;

  if (currentOrganization.userId === user.id) {
    // owner
    if (billingInfo.plan && billingInfo.plan !== BillingInfoPlan.TRIAL) {
      openGenAiAddonPopup();
    } else {
      // do not have subscription
      getOpenPaywallPopup({
        openModal,
        payload: {
          payloadType: PopupType.PAYWALL,
          mode: 'upgrade',
          title: 'Work Your Magic: Unlock All AI Powers Now!',
          reason: PaywallPopupReason.AI_LIMIT,
          orgId: currentOrganization.globalId,
        },
      });
    }
  } else {
    openModal({
      title: 'Free AI requests limit reached',
      content: PopupType.DIALOG,
      closeButtonSize: 18,
      payload: {
        payloadType: PopupType.DIALOG,
        text: 'AI feature is available with AI addon. Contact the organization owner for more details.',
        buttons: [
          {
            text: 'OK',
            main: true,
          },
        ],
      },
    });
  }
}

export function getCookieValue(key: string) {
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1);
    }
    if (cookie.indexOf(key + '=') === 0) {
      return cookie.substring(key.length + 1, cookie.length);
    }
  }
  return '';
}

interface StartWhisperAiPayload {
  usage: AiUsage;
  sessionId: string;
  orgId: string;
  workspaceId: string;
  attachmentId: string;
  prompt: string;
  cmd: string;
  aiServiceUrl: string | undefined;
  popupParams: ShowAILmitPopupParams;
  cancelId: string;
  onMessage: (val: string) => void;
}
export const startWhisperAi = ({
  usage,
  sessionId,
  orgId,
  workspaceId,
  attachmentId,
  prompt,
  cmd,
  aiServiceUrl,
  popupParams,
  cancelId,
  onMessage,
}: StartWhisperAiPayload) => {
  //TODO: get aiAccess variable from gateway;
  const controller = new AbortController();
  return new GetMessageTask(() => {
    return new Promise((resolve, reject) => {
      if (usage && usage.current >= usage.max) {
        showAiLimitPopup(popupParams);
        return reject({ name: 'LimitError' });
      }

      let markdown = '';
      const tasks: string[] = [];
      const nimbusOps: Record<string, unknown>[] = [];
      const signal = controller.signal;
      fetch(`${aiServiceUrl}/whisper`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'everhelper-session-id': getCookieValue('eversessionid'),
        },
        body: JSON.stringify({
          sessionId,
          orgId,
          workspaceId,
          attachmentId,
          prompt,
          cmd,
          cancelId,
        }),
        signal,
      })
        .then(async (response) => {
          if (response.status !== 200) {
            const err = await response.json();
            if (err.name === 'LimitError') {
              showAiLimitPopup(popupParams);
              return reject(err);
            }
          }

          function processLine(line: string) {
            try {
              const decoded = b64DecodeUnicode(line);
              if (decoded) {
                const [markdownChunk, contentChunk, listsChunk] = JSON.parse(decoded);
                if (contentChunk) {
                  nimbusOps.push(...(contentChunk as Record<string, unknown>[]));
                }
                markdown += markdownChunk;
                if (listsChunk.length) {
                  tasks.push(...(listsChunk as string[]));
                }
                onMessage(markdownChunk);
              }
            } catch (err) {
              console.warn(err);
            }
          }
          const reader = response?.body?.getReader();
          if (reader) {
            let accumulatedData = '';
            // eslint-disable-next-line no-constant-condition
            while (true) {
              const { done, value } = await reader.read();
              if (done) {
                if (accumulatedData) {
                  processLine(accumulatedData);
                }
                resolve({ markdown, content: nimbusOps, tasks });
                return;
              }
              const lines = new TextDecoder().decode(value);
              accumulatedData += lines;
              let newlineIndex: number;
              while ((newlineIndex = accumulatedData.indexOf('\n')) !== -1) {
                const line = accumulatedData.slice(0, newlineIndex);
                accumulatedData = accumulatedData.slice(newlineIndex + 1);
                if (line) {
                  processLine(line);
                }
              }
            }
          }
        })
        .catch((err) => reject(err));
    });
  }, controller);
};
